import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { styled } from 'react-free-style';
import { connect } from 'react-redux';
import * as elements from '../../styles/elements';

import { Spinner } from '@united-talent-agency/components';

import Wrapper from './Wrapper';
import CallListWrapper from './CallListWrapper';

import { datadogRum } from '@datadog/browser-rum';
import { useDebouncedCallback } from 'use-debounce';
import { notify } from 'react-notify-toast';
import {
  fullRefresh as handleFullRefreshResult,
  loadMore as handleLoadMoreResult,
  handleInternalUpdate,
  handleBulkUpdate,
} from './CallListWrapper/Helper';
import useQueryParamsSync from '../../hooks/useQueryParamsSync';
import {
  useCallRecordsQuery,
  useDeskMutatedSubscription,
  useLoginMutation,
  CallRecordsDocument,
} from '../../generated/graphql';
import { useApolloClient } from '@apollo/client';
import { today } from '../../support/date';
import { WS_STATUS_EVENT } from '../../lib/apollo-client';

const WIDTH_LIMIT = 1240;
const REFRESH_RATE = (Number(process.env.REACT_APP_REFRESH_RATE_SECONDS) || 10) * 1000;
const DEFAULT_CALL_FETCH_LIMIT = 20;

const Component = ({ user, styles }) => {
  // Apollo client for cache watching
  const apolloClient = useApolloClient();

  // Refs
  const prevWidth = useRef(WIDTH_LIMIT + 1);
  const latestRefreshTimeRef = useRef();
  const fetchCountsRef = useRef({});
  const isLoadingRef = useRef(true);

  // States
  const [fetchCounts, setFetchCounts] = useState({});
  const [totalCounts, setTotalCounts] = useState({});
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [selectAll, setSelectAll] = useState(false);
  const [selectExcept, setSelectExcept] = useState(new Set());
  const [callsToDo, setCallsToDo] = useState([]);
  const [statusOfLoadingMore, setStatusOfLoadingMore] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dataDogSet, setDataDogSet] = useState(false); //  Flag used to register what User is active in the application for DataDog.

  // Sometimes it comes like a desk object and not just only ids. So, lets change it to be an array of ids.
  const deskIds = useMemo(
    () => user?.deskIds?.map((deskId) => deskId?._id?.toString?.() ?? deskId?.toString?.()),
    [user?.deskIds]
  );

  const [loginMutation, { called, loading, data: loginData }] = useLoginMutation();

  useEffect(() => {
    const email = user?.email;
    if (!email) {
      return;
    }

    loginMutation({
      variables: {
        email,
        loginInterface: 'WEB',
      },
    });
  }, [loginMutation, user.email]);

  const loggedIn = useMemo(
    () => called && !loading && loginData?.login?.success,
    [called, loading, loginData?.login?.success]
  );

  const { desk, search, deskStatuses, updateQuery, updateParams, changeDesk, refreshStore } =
    useQueryParamsSync({
      userId: user?._id,
      deskIds,
      initialPhoneSheetFilters: user?.phoneSheetFilter,
      loggedIn,
    });

  const { refetch: refetchCallRecords } = useCallRecordsQuery({
    skip: true,
  });

  const { data: subscriptionData } = useDeskMutatedSubscription({
    skip: !loggedIn || !desk?._id,
    shouldResubscribe: true,
    variables: {
      deskId: desk?._id,
    },
  });

  // Get hideFutureCalls from subscription data if available, otherwise from desk state
  const hideFutureCalls = useMemo(
    () =>
      subscriptionData?.deskMutated?.settings?.hideFutureCalls ?? desk?.settings?.hideFutureCalls,
    [subscriptionData?.deskMutated?.settings?.hideFutureCalls, desk?.settings?.hideFutureCalls]
  );

  // Apply future calls filter as a memoized value that recalculates when dependencies change
  const filteredCallsToDo = useMemo(() => {
    if (!hideFutureCalls) {
      return callsToDo;
    }

    const todayStart = today();

    return callsToDo?.filter?.((callRecord) => {
      const callDate = new Date(callRecord?.occurrence_date);

      // Strip time from call date to get date-only comparison
      const callDateOnly = new Date(
        callDate.getFullYear(),
        callDate.getMonth(),
        callDate.getDate()
      );

      // Include calls from today and past dates (hide only future dates)
      return callDateOnly <= todayStart;
    });
  }, [callsToDo, hideFutureCalls]);

  // Calculate filtered fetch counts for display (excludes hidden future calls)
  const filteredFetchCounts = useMemo(() => {
    const counts = {};
    filteredCallsToDo?.forEach((call) => {
      counts[call.status] = (counts[call.status] || 0) + 1;
    });
    return counts;
  }, [filteredCallsToDo]);

  // Clean up selectExcept when hideFutureCalls changes to handle hidden records
  useEffect(() => {
    if (callsToDo.length === 0) {
      return;
    }

    // Get set of visible record IDs after filtering
    const visibleIds = new Set(filteredCallsToDo?.map((call) => call._id) || []);

    // Get IDs of hidden records (present in callsToDo but not in filteredCallsToDo)
    const hiddenIds = callsToDo.filter((call) => !visibleIds.has(call._id)).map((call) => call._id);

    if (hiddenIds.length === 0) {
      return;
    }

    // Use callback form to avoid dependency on selectExcept
    setSelectExcept((prevSelectExcept) => {
      let needsUpdate = false;
      const updatedSelectExcept = new Set(prevSelectExcept);

      if (selectAll) {
        // When selectAll=true, selectExcept contains DESELECTED items
        // We need to ADD hidden IDs to selectExcept so they won't be operated on
        hiddenIds.forEach((id) => {
          if (!updatedSelectExcept.has(id)) {
            updatedSelectExcept.add(id);
            needsUpdate = true;
          }
        });
      } else {
        // When selectAll=false, selectExcept contains SELECTED items
        // We need to REMOVE hidden IDs from selectExcept so they won't be operated on
        hiddenIds.forEach((id) => {
          if (updatedSelectExcept.has(id)) {
            updatedSelectExcept.delete(id);
            needsUpdate = true;
          }
        });
      }

      return needsUpdate ? updatedSelectExcept : prevSelectExcept;
    });
  }, [hideFutureCalls, filteredCallsToDo, callsToDo, selectAll]);

  useEffect(() => {
    // if no configuration is active, DD should be set to true
    if (!datadogRum.getInitConfiguration()) {
      setDataDogSet(true);
    }
  }, []);

  useEffect(() => {
    const windowResizeListener = () => {
      // While resizing, if screen size got smaller than mediumBreakpoint, filters should be collapsed
      if (prevWidth.current > WIDTH_LIMIT && window.innerWidth <= WIDTH_LIMIT) {
        setFiltersExpanded(false);
      }
      prevWidth.current = window.innerWidth;
    };

    windowResizeListener();

    window.addEventListener('resize', windowResizeListener);

    return () => {
      window.removeEventListener('resize', windowResizeListener);
    };
  }, []);

  useEffect(() => {
    // Flag allows this to fire 1x per user-login, as desired.
    if (user && user.azure_id && !dataDogSet) {
      datadogRum.setUser({
        id: user.azure_id,
        name: `${user.last_name}, ${user.first_name}`,
        email: user.email,
      });

      datadogRum.startSessionReplayRecording();

      setDataDogSet(true);
    }
  }, [dataDogSet, user]);

  // Keep refs in sync with state so fullRefresh reads latest values without depending on them
  useEffect(() => {
    fetchCountsRef.current = fetchCounts;
  }, [fetchCounts]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  const fullRefresh = useCallback(
    (_search = search) => {
      if (!loggedIn || !user?.email || !user?.azure_id || !_search?.deskId || !deskStatuses) {
        return;
      }

      const currentTime = new Date().getTime();
      latestRefreshTimeRef.current = currentTime;

      const deskStatuesesNames = deskStatuses?.map((s) => s.status);
      let statuses = deskStatuesesNames ?? [];
      if (_search?.filter?.length) {
        statuses = deskStatuesesNames.filter((status) => _search?.filter.includes(status));
      }

      const currentFetchCounts = fetchCountsRef.current;
      const promises = statuses
        ?.filter((_status) => _status !== '')
        ?.map((item) => {
          let limit = DEFAULT_CALL_FETCH_LIMIT;
          if (!!currentFetchCounts[item] && DEFAULT_CALL_FETCH_LIMIT < currentFetchCounts[item]) {
            limit = currentFetchCounts[item];
          }

          return refetchCallRecords({
            email: user?.email,
            azureId: user?.azure_id,
            where: {
              deskId: _search?.deskId,
              search: _search?.query,
              statuses: [item],
              favorite: _search?.favorite,
            },
            order: {
              statusThenOccurrence: true,
            },
            skip: 0,
            take: limit,
          });
        });

      return Promise.all(promises)
        .then((results) => {
          // It invalidates the results if a newer request is made.
          if (currentTime !== latestRefreshTimeRef.current) {
            return;
          }

          if (isLoadingRef.current) {
            setIsLoading(false);
          }
          const parsedResults = results.map((result) => ({
            data: result?.data?.callRecords?.callRecords,
            originalData: result?.data?.callRecords?.callRecords,
            meta: result?.data?.callRecords?.meta,
          }));

          return handleFullRefreshResult(
            parsedResults,
            deskStatuesesNames,
            setFetchCounts,
            setTotalCounts,
            setCallsToDo
          );
        })
        .catch(() => {
          // It invalidates the results if a newer request is made.
          if (currentTime !== latestRefreshTimeRef.current) {
            return;
          }

          if (isLoadingRef.current) {
            setIsLoading(false);
          }
          notify.show('Failed to load calls', 'error');
        });
    },
    [deskStatuses, loggedIn, refetchCallRecords, search, user?.azure_id, user?.email]
  );

  const debouncedFullRefresh = useDebouncedCallback(fullRefresh, 100, {
    leading: false,
    trailing: true,
    maxWait: REFRESH_RATE,
  });

  const resetSearch = useCallback(() => {
    const newData = { filter: undefined, favorite: undefined, query: undefined };
    updateParams(newData);
    fullRefresh({
      ...search,
      ...newData,
    });
  }, [fullRefresh, search, updateParams]);

  const handleOnChangeDesk = useCallback(
    async (selectedDesk) => {
      changeDesk(selectedDesk?._id);
    },
    [changeDesk]
  );

  const navigate = (_search) => {
    updateParams(_search);
    fullRefresh({
      ...search,
      ..._search,
    });
  };

  const handleUpdateQuery = (query) => {
    if (query === search.query) {
      return;
    }

    updateQuery(query);
    fullRefresh({
      ...search,
      query,
    });
  };

  const onEditDone = useCallback(
    (updatedCall, originalCall, bulkOperation) => {
      // Handle bulk operations
      if (bulkOperation) {
        handleBulkUpdate(
          bulkOperation,
          fetchCounts,
          totalCounts,
          setFetchCounts,
          setTotalCounts,
          callsToDo,
          setCallsToDo
        );
      } else {
        // Handle single record updates
        handleInternalUpdate(
          updatedCall,
          originalCall,
          fetchCounts,
          totalCounts,
          setFetchCounts,
          setTotalCounts,
          callsToDo,
          setCallsToDo
        );
      }
    },
    [fetchCounts, totalCounts, callsToDo]
  );

  /**
   * Load more calls per status
   *
   * @param {Search} search: params for search
   * @param {string} status: status of calls to load
   **/
  const loadMore = useCallback(
    (_status) => {
      if (!loggedIn) {
        return;
      }

      setStatusOfLoadingMore(_status);
      refetchCallRecords({
        email: user.email,
        azureId: user.azure_id,
        where: {
          deskId: search?.deskId,
          statuses: [_status],
          search: search?.query,
          favorite: search?.favorite,
        },
        order: {
          statusThenOccurrence: true,
        },
        skip: fetchCounts[_status],
      })
        .then((result) => {
          const parsedResult = {
            data: result?.data?.callRecords?.callRecords,
            originalData: result?.data?.callRecords?.callRecords,
            meta: result?.data?.callRecords?.meta,
          };
          handleLoadMoreResult(
            parsedResult,
            _status,
            fetchCounts,
            setFetchCounts,
            callsToDo,
            setCallsToDo
          );
          setStatusOfLoadingMore((oldState) =>
            oldState?.filter?.((loadingStatus) => loadingStatus !== _status)
          );
        })
        .catch(() => {
          setStatusOfLoadingMore((oldState) =>
            oldState?.filter?.((loadingStatus) => loadingStatus !== _status)
          );
          notify.show('Failed to load more calls', 'error');
        });
    },
    [
      callsToDo,
      fetchCounts,
      loggedIn,
      refetchCallRecords,
      search?.deskId,
      search?.favorite,
      search?.query,
      user.azure_id,
      user.email,
    ]
  );

  /**
   * Register a cache watch for CallRecordsDocument to trigger fullRefresh on cache invalidation.
   *
   * This implementation simulates the same refetch behavior that would be caused by cache invalidation,
   * but instead triggers our manual fullRefresh function. We use this approach because there's no
   * other way to make multiple requests (one for each status) using just the useCallRecordsQuery hook.
   *
   * The challenge is that we need to fetch data for each status separately with different limits
   * (based on fetchCounts), which requires multiple API calls. Since useCallRecordsQuery can only
   * make one query at a time, we use manual refetch calls in fullRefresh() to handle the
   * multi-status data fetching.
   *
   * This cache watch ensures that when mutations complete and Apollo invalidates the
   * CallRecordsDocument cache, our fullRefresh() function is automatically triggered,
   * maintaining the same cache invalidation behavior while keeping our custom multi-status
   * data fetching logic intact.
   */
  useEffect(() => {
    if (!loggedIn || !apolloClient || !deskStatuses) {
      return;
    }

    // Register the query with Apollo Client's cache by creating a watchQuery
    const queryVariables = {
      where: {
        deskId: search?.deskId || '',
        search: search?.query,
        statuses: [],
        favorite: search?.favorite,
      },
      azureId: user?.azure_id || '',
      email: user?.email || '',
      order: { statusThenOccurrence: true },
      skip: 0,
      take: DEFAULT_CALL_FETCH_LIMIT,
    };

    const observableQuery = apolloClient.watchQuery({
      query: CallRecordsDocument,
      variables: queryVariables,
      fetchPolicy: 'cache-only', // Don't fetch, just register for cache watching
      errorPolicy: 'ignore',
      pollInterval: 0, // Disable polling
    });

    // Subscribe to changes
    const subscription = observableQuery.subscribe({
      next: () => {
        fullRefresh();
      },
      error: (error) => {
        // Ignore cache-only errors since we're just watching
        console.log('Cache watch error (expected for cache-only):', error);
      },
    });

    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deskStatuses,
    apolloClient,
    loggedIn,
    user?.azure_id,
    user?.email,
    search?.deskId,
    search?.query,
    search?.favorite,
  ]);

  useEffect(() => {
    if (subscriptionData) {
      refreshStore();
    }
  }, [subscriptionData, refreshStore]);

  useEffect(() => {
    if (deskStatuses) {
      debouncedFullRefresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionData, deskStatuses]);

  // Polling fallback: when WebSocket disconnects, poll at REFRESH_RATE until it reconnects
  useEffect(() => {
    let pollingInterval = null;

    const handleWsStatus = (event) => {
      const { connected } = event.detail;

      if (!connected && !pollingInterval) {
        pollingInterval = setInterval(() => {
          debouncedFullRefresh();
        }, REFRESH_RATE);
      }

      if (connected && pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
      }
    };

    window.addEventListener(WS_STATUS_EVENT, handleWsStatus);

    return () => {
      window.removeEventListener(WS_STATUS_EVENT, handleWsStatus);
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [debouncedFullRefresh]);

  if (isLoading || !loggedIn) {
    return (
      <div className={styles.spinner}>
        <Spinner size={60} />
      </div>
    );
  }

  if (!user?.deskIds?.length) {
    return (
      <h5 className="m-4">
        No current office groups configured. Please contact the Service Desk at x3900
      </h5>
    );
  }

  if (!search?.deskId) {
    return <></>;
  }

  return (
    <Wrapper
      user={user}
      desk={desk}
      search={search}
      filteredFetchCounts={filteredFetchCounts}
      totalCounts={totalCounts}
      deskStatuses={deskStatuses}
      filtersExpanded={filtersExpanded}
      navigate={navigate}
      resetSearch={resetSearch}
      updateQuery={handleUpdateQuery}
      onChangeDesk={handleOnChangeDesk}
      setSelectAll={setSelectAll}
      setSelectExcept={setSelectExcept}
      changeFiltersExpanded={setFiltersExpanded}
    >
      <CallListWrapper
        user={user}
        desk={desk}
        search={search}
        callsToDo={filteredCallsToDo}
        selectAll={selectAll}
        fetchCounts={fetchCounts}
        totalCounts={totalCounts}
        deskStatuses={deskStatuses}
        selectExcept={selectExcept}
        statusOfLoadingMore={statusOfLoadingMore}
        loadMore={loadMore}
        onEditDone={onEditDone}
        setSelectAll={setSelectAll}
        setTotalCounts={setTotalCounts}
        setSelectExcept={setSelectExcept}
        hideFutureCalls={hideFutureCalls}
      />
    </Wrapper>
  );
};

const withStyles = styled({
  spinner: {
    display: 'flex',
    height: '100vh',
    alignItems: 'center',
    justifyContent: 'center',
  },
  link: elements.link,
});

const withState = connect(({ user }) => {
  // This is stored here because it must be set right after logging in.
  localStorage.setItem('uta.azure_id', user.azure_id);
  localStorage.setItem('uta.user_email', user.email);
  localStorage.setItem('uta.outlook_permissions', user.outlook_permissions);

  return { user };
});

const Home = withStyles(withState(Component));

export default Home;
